/**
 * Shared "is this event over?" rule (1.8.7).
 *
 * The Calendar "Your plan" list and the Profile Raid Radar previously each
 * decided independently whether an event was still live, and they disagreed:
 * the same event could show as a past item in the plan while still reading
 * "NOW" in Raid Radar. This module is the single source of truth both surfaces
 * import so they can never drift again.
 *
 * Event times are floating Pokémon GO wall-clock (a 2 PM event is 2 PM in the
 * user's OWN timezone). eventCountdownMs already recovers that wall-clock from
 * every feed shape (date-only, bare local, and the server's ...Z form) and
 * rebuilds it as a device-local epoch, so we reuse it here rather than
 * re-parsing. "Over" is evaluated on the event's END time; an event with no
 * end falls back to start + a default duration, mirroring the add-to-calendar
 * range logic.
 */
import { eventCountdownMs } from "./calendarLinks";

const DEFAULT_DURATION_MS = 3 * 60 * 60 * 1000;

export interface RecencyEvent {
  start?: string | null;
  end?: string | null;
}

/**
 * Device-local epoch (ms) for when the event ends. Prefers the explicit end;
 * falls back to start + 3h. Returns NaN when neither time is parseable, so
 * callers can treat "unknown" as "keep it" rather than dropping it.
 */
export function eventEndMs(ev: RecencyEvent): number {
  if (ev.end && ev.end.length > 0) {
    const endMs = eventCountdownMs(ev.end);
    if (!Number.isNaN(endMs)) return endMs;
  }
  if (ev.start && ev.start.length > 0) {
    const startMs = eventCountdownMs(ev.start);
    if (!Number.isNaN(startMs)) return startMs + DEFAULT_DURATION_MS;
  }
  return NaN;
}

/**
 * True when the event's END time is strictly in the past. Unknown times are
 * treated as NOT over (keep them), so a malformed feed entry is never silently
 * dropped from a plan or radar.
 */
export function isEventOver(ev: RecencyEvent, now: number = Date.now()): boolean {
  const endMs = eventEndMs(ev);
  if (Number.isNaN(endMs)) return false;
  return endMs < now;
}

/**
 * Device-local epoch (ms) for when the event starts, for ascending date sorts.
 * Returns Number.POSITIVE_INFINITY when the start is unknown so time-less items
 * sort to the end rather than jumping to the top.
 */
export function eventStartMs(ev: RecencyEvent): number {
  if (ev.start && ev.start.length > 0) {
    const startMs = eventCountdownMs(ev.start);
    if (!Number.isNaN(startMs)) return startMs;
  }
  return Number.POSITIVE_INFINITY;
}
